import React from 'react';
import { translate } from 'react-i18next';

import CBMediaQuery from './CBMediaQuery';
import CBPromptModal from './CBPromptModal';
import CBInputField from './form_components/CBInputField';
import CBDisabledBadge from './CBDisabledBadge';
import { getStyle } from '../../utils/utils';

/**
 * Banner + disable/enable button shown in the staff detail forms for a workshop or
 * an organization. Disabling asks for confirmation and an optional reason, and spells
 * out the consequences, because it revokes access for every user of the entity.
 *
 * Props:
 *   entity      - the workshop / organization object (needs is_disabled, disabled_at,
 *                 disabled_reason, disabled_by_name)
 *   entityType  - 'workshop' or 'organization', selects the confirmation wording
 *   onSetDisabled(disabled, reason) - called when the staff user confirms
 */
class CBDisableEntityControl extends React.Component {
  state = {
    modalIsOpen: false,
    reason: '',
  };

  openModal = () => {
    const { entity } = this.props;
    this.setState({
      modalIsOpen: true,
      reason: entity && entity.disabled_reason ? entity.disabled_reason : '',
    });
  };

  closeModal = () => this.setState({ modalIsOpen: false, reason: '' });

  // CBInputField reports changes as { target: <field name>, value }, not a DOM event
  changeReason = (change) => this.setState({ reason: change.value });

  performAction = () => {
    const { entity, onSetDisabled } = this.props;
    const { reason } = this.state;

    onSetDisabled(!entity.is_disabled, reason);
    this.closeModal();
  };

  formatDisabledAt = () => {
    const { entity } = this.props;

    if (!entity.disabled_at) {
      return '';
    }

    const date = new Date(entity.disabled_at);
    if (isNaN(date.getTime())) {
      return '';
    }

    const pad = (value) => (value < 10 ? `0${value}` : `${value}`);
    return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
  };

  renderBanner = (screenSize) => {
    const { entity, t } = this.props;

    const parts = [];
    const disabledAt = this.formatDisabledAt();
    if (disabledAt) {
      parts.push(t('disabledOn', { date: disabledAt }));
    }
    if (entity.disabled_by_name) {
      parts.push(t('disabledByWhom', { name: entity.disabled_by_name }));
    }
    if (entity.disabled_reason) {
      parts.push(t('disabledBecause', { reason: entity.disabled_reason }));
    }

    return (
      <div style={getStyle(screenSize, styles, 'banner')}>
        <CBDisabledBadge />
        <p style={getStyle(screenSize, styles, 'bannerText')}>
          {parts.length ? parts.join(' — ') : t('disabledBannerFallback')}
        </p>
      </div>
    );
  };

  render() {
    const { entity, entityType, t } = this.props;
    const { modalIsOpen, reason } = this.state;

    if (!entity || !entity.id) {
      return null;
    }

    const isDisabled = !!entity.is_disabled;
    const confirmTextKey = isDisabled
      ? 'enableConfirmText'
      : (entityType === 'workshop' ? 'disableWorkshopConfirmText' : 'disableOrganizationConfirmText');

    return (
      <CBMediaQuery>
        {screenSize => (
          <div style={getStyle(screenSize, styles, 'container')}>
            {isDisabled && this.renderBanner(screenSize)}

            <div
              style={isDisabled
                ? getStyle(screenSize, styles, 'enableButton')
                : getStyle(screenSize, styles, 'disableButton')}
              onClick={this.openModal}
            >
              {isDisabled ? t('enableButton') : t('disableButton')}
            </div>

            <CBPromptModal
              modalIsOpen={modalIsOpen}
              closeModal={this.closeModal}
              performAction={this.performAction}
              title={isDisabled ? t('enableConfirmTitle') : t('disableConfirmTitle')}
              text={t(confirmTextKey, { name: entity.name })}
              buttonText={isDisabled ? t('enableButton') : t('disableButton')}
            >
              {!isDisabled && (
                <CBInputField
                  labelText={t('disableReasonLabel')}
                  value={reason}
                  onChange={this.changeReason}
                  name='disabled_reason'
                />
              )}
            </CBPromptModal>
          </div>
        )}
      </CBMediaQuery>
    );
  }
}

const styles = {
  default: {
    container: {
      marginTop: 24,
      marginBottom: 16,
    },
    banner: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: 'rgba(231, 76, 60, 0.15)',
      border: '1px solid #e74c3c',
      borderRadius: 6,
      padding: 12,
      marginBottom: 16,
    },
    bannerText: {
      fontFamily: 'TitilliumWeb-Light',
      color: '#FFFFFF',
      fontSize: 14,
      marginLeft: 12,
      marginBottom: 0,
    },
    disableButton: {
      display: 'inline-block',
      cursor: 'pointer',
      border: '2px solid #e74c3c',
      color: '#e74c3c',
      borderRadius: 50,
      padding: '8px 24px',
      fontFamily: 'TitilliumWeb-SemiBold',
      fontSize: 14,
    },
    enableButton: {
      display: 'inline-block',
      cursor: 'pointer',
      border: '2px solid #00f600',
      color: '#00f600',
      borderRadius: 50,
      padding: '8px 24px',
      fontFamily: 'TitilliumWeb-SemiBold',
      fontSize: 14,
    },
  },
  small: {
    banner: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    bannerText: {
      marginLeft: 0,
      marginTop: 8,
    },
  },
};

export default translate('SettingsView')(CBDisableEntityControl);
